import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Outlet, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  Paper,
  Box,
  Grid,
  BottomNavigation,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { API, Auth } from '../../services/amplifyCompat';
import { BaseComponent } from '../../prop-types/component';
import { UserInfoContext } from '../../hooks/use-user-info-context';
import { updateMenuState } from '../../actions/menuActions';
import { getSingleUserByEmail } from '../../graphql/queries';
import { createUser } from '../../graphql/mutations';
import { usePageContainerStyles } from '../../styles/page-container';
import Navbar from '../../components/Navbar';
import { AppNav } from '../AppNav';
import { getUserEmail } from '../../utils/signup-flow-type';
import { isTransientNetworkError } from '../../utils/network-retry';
import { captureException, startSpan } from '../../monitoring/sentry';
import useTranslation from '../../components/customHooks/translations';

// Constants for attribute names
const COGNITO_ATTRIBUTES = {
  ID: 'custom:id',
  TYPE: 'custom:type',
  FIRST_LOGIN: 'custom:firstLogin'
};

// Error handling wrapper
const handleApiError = (error) => {
  console.error('API Error:', error);
  // You might want to add proper error handling/notification here
  throw error;
};

const isCapturedGraphqlRequestError = (error) =>
  error?.name === 'GraphQLRequestError' || error?.name === 'GraphQLAuthorizationError';

function PageContainer(props) {
  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down('md'));
  const { menuEnabled, updateMenuState } = props;

  const { classes } = usePageContainerStyles();
  const t = useTranslation();

  const navigate = useNavigate();
  const [user, setUser] = useState();
  const [userType, setUserType] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const userIsAdmin = userType === 'Admin';

  // Fetches user from database
  const fetchUserFromDatabase = async (email) => {
    try {
      const res = await API.graphql({
        query: getSingleUserByEmail,
        variables: { email }
      });
      return res.data.getSingleUserByEmail;
    } catch (error) {
      handleApiError(error);
    }
  };

  // Creates new user in database
  const createUserInDatabase = async (cognitoUserEntry, email) => {
    try {
      const name = cognitoUserEntry.attributes.name
        || cognitoUserEntry.attributes.preferred_username
        || (email ?? "").split("@")[0] || "User";
      const userRes = await API.graphql({
        query: createUser,
        variables: {
          name,
          username: cognitoUserEntry.attributes.preferred_username,
          email
        }
      });
      return userRes.data.createUser;
    } catch (error) {
      handleApiError(error);
    }
  };

  // Updates Cognito attributes
  const updateCognitoAttributes = async (cognitoUserEntry, attributes) => {
    try {
      await Auth.updateUserAttributes(cognitoUserEntry, attributes);
    } catch (error) {
      handleApiError(error);
    }
  };

  // Synchronizes user data between Cognito and database
  const syncUserData = async (cognitoUserEntry, databaseUser) => {
    const cognitoId = cognitoUserEntry.attributes[COGNITO_ATTRIBUTES.ID];
    const databaseId = databaseUser.user_id.toString();

    if (!cognitoId || cognitoId !== databaseId) {
      await updateCognitoAttributes(cognitoUserEntry, {
        [COGNITO_ATTRIBUTES.ID]: databaseId
      });
    }
  };

  // Main function to get and sync user data
  const getCognitoUser = async () => {
    return startSpan(
      {
        op: 'auth.sync',
        name: 'Sync Cognito user and profile',
      },
      async (span) => {
        try {
          setIsLoading(true);
          setError(null);

          const cognitoUserEntry = await Auth.currentAuthenticatedUser();
          const email = getUserEmail(cognitoUserEntry);

          // Get or create user in database
          let databaseUser = await fetchUserFromDatabase(email);

          if (!databaseUser) {
            const newUser = await createUserInDatabase(cognitoUserEntry, email);
            await updateCognitoAttributes(cognitoUserEntry, {
              [COGNITO_ATTRIBUTES.FIRST_LOGIN]: 'false',
              [COGNITO_ATTRIBUTES.ID]: newUser.user_id.toString()
            });
            databaseUser = newUser;
          }

          // Sync user data (custom:id to cognito)
          await syncUserData(cognitoUserEntry, databaseUser);

          span.setAttribute('user.type', cognitoUserEntry.attributes[COGNITO_ATTRIBUTES.TYPE] ?? 'unknown');
          span.setAttribute('user.id', databaseUser.user_id?.toString?.() ?? 'unknown');

          // Update state
          setUserType(cognitoUserEntry.attributes[COGNITO_ATTRIBUTES.TYPE]);
          setUser(databaseUser);
        } catch (error) {
          if (
            !isCapturedGraphqlRequestError(error) &&
            !isTransientNetworkError(error)
          ) {
            captureException(error, {
              tags: {
                module: 'PageContainer',
                flow: 'auth.sync',
              },
            });
          }
          setError(error);
        } finally {
          setIsLoading(false);
        }
      }
    );
  };

  useEffect(() => {
    getCognitoUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close mobile drawer after nav; Link handles navigation (do not pass click
  // event to navigate — that double-pushes history with the current location).
  const handleMenuNavItem = () => {
    if (mobileView) updateMenuState(false);
  };

  // Set drawer state from viewport instead of toggling (toggle flipped randomly
  // after rotate / resize).
  useEffect(() => {
    updateMenuState(!mobileView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileView]);

  return (
    <UserInfoContext.Provider
      value={{
        user,
        userType,
        userIsAdmin,
        setUser,
        isLoading,
        error
      }}
    >
      <Grid direction="column" container>
        <a className={classes.skip_button} href="#main">
          {t?.skip || 'Skip to Content'}
        </a>
        {/* Navbar component, set side menu button parameter -->
        button updates redux state to show/hide left sidebar */}
        <Navbar showSideMenuButton={true} sx={{ position: 'sticky' }} />
        {/* App content example below with sidebar */}
        <Box className={classes.appShell}>
          {/* Side menu component */}
          {!mobileView &&
            <Box
              className={clsx(classes.drawerContainer, {
                [classes.drawerContainerClosed]: !menuEnabled,
              })}
            >
              <Box className={classes.drawerSurface}>
                <AppNav handleMenuNavItem={handleMenuNavItem} />
              </Box>
            </Box>
          }
          <main
            id="main"
            className={classes.content}
          >
            <Outlet />
          </main>
        </Box>
      </Grid>
      {mobileView &&
        <Paper
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 2,
            borderRadius: 0,
            borderLeft: 'none',
            borderRight: 'none',
            borderBottom: 'none',
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          }}
          elevation={0}
        >
          <BottomNavigation
            showLabels
            sx={{
              height: '70px',
              paddingTop: '4px',
              background: (theme) => theme.palette.background.paper,
            }}
          >
            <AppNav handleMenuNavItem={handleMenuNavItem} />
          </BottomNavigation>
        </Paper>
      }
    </UserInfoContext.Provider>
  );
}

PageContainer.propTypes = {
  ...BaseComponent,
  menuEnabled: PropTypes.bool,
  updateMenuState: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  return {
    menuEnabled: state.appState.showSideBar,
  };
};

const mapDispatchToProps = {
  updateMenuState,
};

export default connect(mapStateToProps, mapDispatchToProps)(PageContainer);
